import type {
  MorphemeGlyphTileInkLineStyle,
  MorphemeGlyphTilePronunciationStyle,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui._components.character";

/** Pinyin marker row height (`h-1.5`). Kept short so the glyph can grow large. */
export const MORPHEME_FRONT_BUTTON_PINYIN_DOTS_ROW_HEIGHT = 6;

/** Per-side inset for word rows with three or more characters (`px-0`). */
export const CARD_FRONT_BUTTON_WORD_SIDE_PADDING_PX = 0;

export const CARD_FRONT_BUTTON_WORD_SIDE_PADDING_MIN_CHARACTERS = 3;

// How much of the zone the glyph LINE BOX fills. Kept high so characters render large; the small
// headroom below (not a large one) is what keeps tall glyphs from clipping, so we don't need to shrink
// the font much to avoid the clip.
const GLYPH_FONT_RATIO = 0.95;

/**
 * Single-grapheme tiles fill their zone more aggressively than the multi-character/word paths: the
 * dots overlay the reserved bottom margin, so the glyph can grow large.
 */
const GRAPHEME_TILE_FONT_RATIO = 0.95;

/**
 * Fraction by which the glyph line box exceeds the font size. CJK ink overshoots the em-box top on
 * iOS, so a line box exactly equal to the font size clips the glyph's top — give it just enough
 * headroom to clear that overshoot (NOT a large amount, which would force the font small to keep the
 * line box inside the tile and make the characters look tiny). Because this headroom is a FIXED
 * fraction of the font size, the resulting line-box slack is the same fraction of the font size for
 * every glyph, so a single recentering nudge (see the renderer's GLYPH_INK_RECENTER_RATIO) corrects the
 * off-center float uniformly across all tile sizes.
 */
export const GLYPH_LINE_BOX_HEADROOM_RATIO = 0.1;

/** Line box (lineHeight) for a glyph of the given font size: font size plus a fixed headroom. */
export function getGlyphLineHeight(fontSize: number): number {
  return Math.round(fontSize * (1 + GLYPH_LINE_BOX_HEADROOM_RATIO));
}

/**
 * Largest font size whose LINE BOX (`fontSize × (1 + headroom)`) fits within `lineBoxLimit` px. The line
 * box — not the font size — is what occupies vertical space and can clip/overflow the tile, so when the
 * tile HEIGHT is the binding constraint we must size the font from the line-box budget, not multiply
 * headroom on top of a font already sized to the full height (which overflows by the headroom fraction).
 */
function fontSizeForLineBox(lineBoxLimit: number): number {
  return Math.max(1, Math.floor(lineBoxLimit / (1 + GLYPH_LINE_BOX_HEADROOM_RATIO)));
}

const CHARACTER_GAP_PX = 0;

export function getMorphemeGlyphTileWordSidePaddingPx(characterCount: number): number {
  if (characterCount < CARD_FRONT_BUTTON_WORD_SIDE_PADDING_MIN_CHARACTERS) {
    return 0;
  }
  return CARD_FRONT_BUTTON_WORD_SIDE_PADDING_PX;
}

export function getMorphemeGlyphTileWordGlyphAvailableWidth({
  characterCount,
  contentWidth,
}: {
  characterCount: number;
  contentWidth: number;
}): number {
  const sidePadding = getMorphemeGlyphTileWordSidePaddingPx(characterCount);
  return Math.max(1, contentWidth - sidePadding * 2);
}

export function getMorphemeGlyphTileGlyphStyle({
  availableHeight,
  availableWidth,
}: {
  /** Glyph zone height (tile height minus the dots row when reserved). */
  availableHeight: number;
  availableWidth: number;
}): { fontSize: number; lineHeight: number } {
  const constrainedSize = Math.max(0, Math.min(availableHeight, availableWidth));
  // The ratio sizes the LINE BOX to fill the zone; the font is then backed out of that line-box budget
  // so the box (font + headroom) never exceeds the zone and the glyph can't overflow the tile.
  const fontSize = fontSizeForLineBox(constrainedSize * GLYPH_FONT_RATIO);
  return { fontSize, lineHeight: getGlyphLineHeight(fontSize) };
}

/**
 * Single-grapheme tiles: size the glyph to the zone. The line box is the font size plus a fixed
 * headroom (see getGlyphLineHeight) so the CJK ink never clips at the top; the renderer then nudges
 * the glyph down by a fixed fraction of the font size to recenter the ink within that headroom. The
 * zone reserves a dots-row of margin on both sides (see the content caller) so the bottom dots overlay
 * never collides with the glyph.
 */
export function getMorphemeGlyphTileGraphemeGlyphStyle({
  glyphZoneHeight,
  glyphZoneWidth,
}: {
  glyphZoneHeight: number;
  glyphZoneWidth: number;
}): { fontSize: number; lineHeight: number } {
  const constrainedSize = Math.max(0, Math.min(glyphZoneHeight, glyphZoneWidth));
  // Size the LINE BOX to fill the zone, then back the font out of that budget so font + headroom (+ the
  // renderer's downward recenter nudge, which the reserved dots-row margin absorbs) stay within the zone.
  const fontSize = fontSizeForLineBox(constrainedSize * GRAPHEME_TILE_FONT_RATIO);
  return { fontSize, lineHeight: getGlyphLineHeight(fontSize) };
}

/** Size multi-character glyphs to fit the row width without exceeding height. */
export function getMorphemeGlyphTileMultiCharacterGlyphStyle({
  availableHeight,
  availableWidth,
  characterCount,
}: {
  availableHeight: number;
  availableWidth: number;
  characterCount: number;
}): { fontSize: number; lineHeight: number } {
  const heightStyle = getMorphemeGlyphTileGlyphStyle({ availableHeight, availableWidth });
  if (characterCount <= 0) {
    return heightStyle;
  }

  const gapTotal = Math.max(0, characterCount - 1) * CHARACTER_GAP_PX;
  const widthPerCharacter = Math.max(0, availableWidth - gapTotal) / characterCount;
  const widthFontSize = Math.max(1, Math.floor(widthPerCharacter * GLYPH_FONT_RATIO));

  if (widthFontSize >= heightStyle.fontSize) {
    return heightStyle;
  }
  return { fontSize: widthFontSize, lineHeight: getGlyphLineHeight(widthFontSize) };
}

/**
 * Word-tile glyph sizing for a known square (or measured) content box.
 *
 * One-character words must match grapheme tiles exactly: same dots-row inset on both sides and the
 * same `getMorphemeGlyphTileGraphemeGlyphStyle` path. Multi-character words keep the full
 * content height and share width across the row.
 */
export function getMorphemeGlyphTileWordGlyphStyle({
  characterCount,
  contentHeight,
  contentWidth,
}: {
  characterCount: number;
  contentHeight: number;
  contentWidth: number;
}): { fontSize: number; lineHeight: number } {
  if (characterCount <= 1) {
    return getMorphemeGlyphTileGraphemeGlyphStyle({
      glyphZoneHeight: Math.max(
        1,
        contentHeight - MORPHEME_FRONT_BUTTON_PINYIN_DOTS_ROW_HEIGHT * 2,
      ),
      glyphZoneWidth: Math.max(1, contentWidth),
    });
  }

  return getMorphemeGlyphTileMultiCharacterGlyphStyle({
    availableHeight: Math.max(1, contentHeight),
    availableWidth: getMorphemeGlyphTileWordGlyphAvailableWidth({
      characterCount,
      contentWidth: Math.max(1, contentWidth),
    }),
    characterCount,
  });
}

/** Horizontal inset on each side of a pinyin line (the renderer's `px-0.5`). */
const PINYIN_LINE_INSET_PX = 2;

/**
 * Width budget, in em, that every word tile sizes its pinyin from. The widest syllables of up to
 * five letters with their tone mark (`huáng`, `guāng`) measure 3.03em in Han Composite's Latin
 * (Noto Sans, weight 400); the slack keeps subpixel layout from ellipsizing them. One budget for
 * every tile means a syllable renders at the same size whatever word it is in.
 */
const PINYIN_SYLLABLE_WIDTH_EM = 3.06;

/** The subpixel-layout slack {@link PINYIN_SYLLABLE_WIDTH_EM} leaves over its widest syllable. */
const PINYIN_LINE_SLACK_EM = 0.03;

/**
 * Advance widths, in em, of the letters pinyin is spelled with in Han Composite's Latin. A tone
 * mark doesn't change its vowel's advance, and tone-number `v` is drawn as `ü`. A line wider
 * than the shared budget — `chuang`, `shuang` and `zhuang` (3.51em), or an erhua `r` on a wide
 * syllable (`méngr` 3.15em, `chuángr` 3.92em) — steps only its own tile down. Sizing every tile
 * for them would shrink the whole grid.
 */
const PINYIN_LETTER_WIDTH_EM: Readonly<Record<string, number>> = {
  a: 0.561,
  b: 0.615,
  c: 0.48,
  d: 0.615,
  e: 0.564,
  f: 0.344,
  g: 0.615,
  h: 0.618,
  i: 0.258,
  j: 0.258,
  k: 0.534,
  l: 0.258,
  m: 0.935,
  n: 0.618,
  o: 0.605,
  p: 0.615,
  q: 0.615,
  r: 0.413,
  s: 0.479,
  t: 0.361,
  u: 0.618,
  ü: 0.618,
  v: 0.618,
  w: 0.786,
  x: 0.529,
  y: 0.51,
  z: 0.47,
};
/** An unexpected character in a reading is measured as the widest letter, so it never clips. */
const WIDEST_PINYIN_LETTER_WIDTH_EM = Math.max(...Object.values(PINYIN_LETTER_WIDTH_EM));

/**
 * The tone-number reading of an erhua 儿/兒 (一点儿 `yi1 dian3 r0`): no syllable of its own, but an
 * `r` on the one before it.
 */
export const ERHUA_PINYIN = "r0";

/**
 * Tightest line pitch, in em, for stacked syllables: a tone mark (0.78em above the baseline)
 * still clears a descender (0.24em below) on the line above. Three-syllable words reach it on
 * square tiles at the shared size; four-syllable idioms shrink their font to keep it.
 */
const PINYIN_MIN_LINE_PITCH_EM = 1.1;

/**
 * Line pitch, in em, when the tile has height to spare. The syllables stack as one centered block
 * rather than spreading to the tile edges, so a two-syllable word reads as one word.
 */
const PINYIN_LINE_PITCH_EM = 1.3;

const TONE_DIGIT_REGEX = /\d/u;

/** One stacked line of a word tile's pinyin front. */
export type WordTilePinyinLine = {
  /** The character whose reading the line shows. */
  characterIndex: number;
  /** The next character is an erhua 儿 folded into this line as a trailing `r` (`diǎnr`). */
  erhua: boolean;
};

/**
 * The stacked lines of a word tile's pinyin front: one per syllable, so an erhua 儿 (reading
 * {@link ERHUA_PINYIN}) joins the line above instead of taking its own. 有一点儿 stacks three
 * lines, `yǒu` / `yī` / `diǎnr`.
 */
export function getWordTilePinyinLines(
  toneNumbersPinyinList: readonly (string | null)[],
): WordTilePinyinLine[] {
  const lines: WordTilePinyinLine[] = [];
  for (const [characterIndex, toneNumbersPinyin] of toneNumbersPinyinList.entries()) {
    const previousLine = lines.at(-1);
    if (toneNumbersPinyin === ERHUA_PINYIN && previousLine !== undefined && !previousLine.erhua) {
      previousLine.erhua = true;
      continue;
    }
    lines.push({ characterIndex, erhua: false });
  }
  return lines;
}

/** The width, in em, a stacked line of the given reading needs, slack included. */
function getPinyinLineWidthEm(toneNumbersPinyin: string | null, erhua: boolean): number {
  if (toneNumbersPinyin === null) {
    return PINYIN_SYLLABLE_WIDTH_EM;
  }
  let widthEm = PINYIN_LINE_SLACK_EM;
  for (const letter of `${toneNumbersPinyin.replace(TONE_DIGIT_REGEX, "")}${erhua ? "r" : ""}`) {
    widthEm += PINYIN_LETTER_WIDTH_EM[letter] ?? WIDEST_PINYIN_LETTER_WIDTH_EM;
  }
  return widthEm;
}

/**
 * Word-tile pinyin sizing when syllables stack vertically (one per line, see
 * {@link getWordTilePinyinLines}). Every tile shares one font size, sized so any syllable of up
 * to five letters fits the tile width; a tile with a wider line (a six-letter syllable, or an
 * erhua `r` on a wide one) steps down to fit it, and four-syllable words shrink to fit their
 * lines in the tile height. `lineHeight` is the line pitch: the renderer stacks the lines as a
 * centered block.
 */
export function getMorphemeGlyphTileWordPinyinStyle({
  contentHeight,
  contentWidth,
  toneNumbersPinyinList,
}: {
  contentHeight: number;
  contentWidth: number;
  /** Each character's tone-number reading (`null` while unresolved). */
  toneNumbersPinyinList: readonly (string | null)[];
}): { fontSize: number; lineHeight: number } {
  const lines = getWordTilePinyinLines(toneNumbersPinyinList);
  const lineCount = Math.max(1, lines.length);
  const widthEm = Math.max(
    PINYIN_SYLLABLE_WIDTH_EM,
    ...lines.map(({ characterIndex, erhua }) =>
      getPinyinLineWidthEm(toneNumbersPinyinList[characterIndex] ?? null, erhua),
    ),
  );
  const availableWidth = Math.max(1, contentWidth - PINYIN_LINE_INSET_PX * 2);
  const fontSize = Math.max(
    1,
    Math.floor(
      Math.min(availableWidth / widthEm, contentHeight / (lineCount * PINYIN_MIN_LINE_PITCH_EM)),
    ),
  );
  const lineHeight = Math.max(
    1,
    Math.floor(Math.min(contentHeight / lineCount, fontSize * PINYIN_LINE_PITCH_EM)),
  );
  return { fontSize, lineHeight };
}

/**
 * Han Composite's vertical metrics, in em. A line box at least as tall as the font's natural line
 * height (ascent + descent) centers that height on every platform (CSS half-leading; React Native
 * iOS offsets the baseline to center it), which puts the baseline half the ascent/descent difference
 * below the line box's middle. A shorter line box is not centered on iOS (see the renderer's
 * GLYPH_INK_RECENTER_RATIO), so pronunciation lines never use one.
 */
const FONT_ASCENT_EM = 1.16;
const FONT_DESCENT_EM = 0.288;

/** Tone marks and ascenders (`lì`, `gàn`) top out 0.766em above the baseline. */
const PINYIN_INK_TOP_EM = 0.766;

/**
 * The ideographic face that full-height characters (十, 自, 丨) fill: 0.84em above the baseline to
 * 0.08em below it. Shorter characters (历, 日, 口) sit inside it.
 */
const IDEOGRAPH_FACE_TOP_EM = 0.84;
const IDEOGRAPH_FACE_BOTTOM_EM = 0.08;

/** A pronunciation tile's reading font size, as a fraction of the tile height. */
const PRONUNCIATION_TILE_PINYIN_FONT_RATIO = 0.26;

/**
 * The gap above the reading, between its baseline and the glyph, and below the glyph, in em of the
 * reading's font. It keeps a descender (`g`, `y`, `j` reach 0.24em below the baseline) clear of the
 * glyph's top.
 */
const PRONUNCIATION_TILE_GAP_EM = 0.35;

function getInkLineStyle({
  baselineFromInkTop,
  fontSize,
  inkHeight,
}: {
  baselineFromInkTop: number;
  fontSize: number;
  inkHeight: number;
}): MorphemeGlyphTileInkLineStyle {
  const lineHeight = Math.ceil(fontSize * (FONT_ASCENT_EM + FONT_DESCENT_EM));
  const baselineFromLineBoxTop =
    lineHeight / 2 + ((FONT_ASCENT_EM - FONT_DESCENT_EM) / 2) * fontSize;
  return {
    fontSize,
    inkHeight,
    lineBoxTop: baselineFromInkTop - baselineFromLineBoxTop,
    lineHeight,
  };
}

/**
 * Pronunciation tiles stack the card's reading over its glyph. The renderer sets each line in a box
 * exactly as tall as its ink (tone-mark top to baseline; the ideographic face) and splits the rest
 * of the tile into three equal gaps, so the space above the reading matches the space between its
 * baseline and the glyph's top. The reading scales with the tile and the glyph shrinks to make room.
 */
export function getMorphemeGlyphTilePronunciationStyle({
  glyphStyle,
  toneNumbersPinyin,
}: {
  /** The tile's grapheme glyph style (`getMorphemeGlyphTileGraphemeGlyphStyle`). */
  glyphStyle: { fontSize: number; lineHeight: number };
  toneNumbersPinyin: string;
}): MorphemeGlyphTilePronunciationStyle {
  // The square tile the glyph style was sized for, inverting `getMorphemeGlyphTileGraphemeGlyphStyle`
  // from the middle of the zone heights its floor maps to this font size. The renderer's flex gaps
  // absorb the estimate's sub-pixel error.
  const tileSize =
    ((glyphStyle.fontSize + 0.5) * (1 + GLYPH_LINE_BOX_HEADROOM_RATIO)) / GRAPHEME_TILE_FONT_RATIO +
    MORPHEME_FRONT_BUTTON_PINYIN_DOTS_ROW_HEIGHT * 2;
  const pinyinFontSize = Math.max(1, Math.round(tileSize * PRONUNCIATION_TILE_PINYIN_FONT_RATIO));
  const pinyinInkHeight = pinyinFontSize * PINYIN_INK_TOP_EM;
  const glyphFontSize = Math.max(
    1,
    Math.floor(
      (tileSize - pinyinFontSize * PRONUNCIATION_TILE_GAP_EM * 3 - pinyinInkHeight) /
        (IDEOGRAPH_FACE_TOP_EM + IDEOGRAPH_FACE_BOTTOM_EM),
    ),
  );
  // A reading wider than the tile at the shared size (`chuang` on the smallest tiles) shrinks onto
  // the same baseline, so every glyph in a row stays at the same height.
  const readingFontSize = Math.max(
    1,
    Math.min(
      pinyinFontSize,
      Math.floor(
        (tileSize - PINYIN_LINE_INSET_PX * 2) / getPinyinLineWidthEm(toneNumbersPinyin, false),
      ),
    ),
  );
  return {
    glyph: getInkLineStyle({
      baselineFromInkTop: glyphFontSize * IDEOGRAPH_FACE_TOP_EM,
      fontSize: glyphFontSize,
      inkHeight: glyphFontSize * (IDEOGRAPH_FACE_TOP_EM + IDEOGRAPH_FACE_BOTTOM_EM),
    }),
    pinyin: getInkLineStyle({
      baselineFromInkTop: pinyinInkHeight,
      fontSize: readingFontSize,
      inkHeight: pinyinInkHeight,
    }),
  };
}
